import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import api from "../lib/api";
import { Sparkle, MapPin, Heart, ChatCircleDots, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import PremiumTeaser from "../components/PremiumTeaser";
import { useSavedReportLoader } from "../lib/useSavedReport";
import { useAuth } from "../context/AuthContext";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

/** Compact birth-details sub-form used twice (boy + girl) on this page. */
function PartnerForm({ label, accent, value, onChange, testidPrefix }) {
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const setField = (k, v) => onChange({ ...value, [k]: v });

  const onPlaceChange = (val) => {
    onChange({ ...value, place: val, timezone: "" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSearching(false);
      setShowSuggestions(trimmed.length > 0);
      return;
    }
    setSearching(true);
    setShowSuggestions(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/places/search`, { params: { q: trimmed } });
        if (myReqId !== reqIdRef.current) return;
        setSuggestions(Array.isArray(data) ? data : []);
        setShowSuggestions(true);
      } catch (_) {
        if (myReqId !== reqIdRef.current) return;
        setSuggestions([]);
      } finally {
        if (myReqId === reqIdRef.current) setSearching(false);
      }
    }, 250);
  };

  const pickSuggestion = (s) => {
    onChange({ ...value, place: s.name, timezone: s.timezone });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className={`card-surface rounded-md p-5 sm:p-6 border-2 ${accent}`} data-testid={`${testidPrefix}-form`}>
      <div className="flex items-center gap-2 label-eyebrow text-gold">
        <Heart size={14} weight="fill" /> {label}
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="label-eyebrow !text-[10px]">Name <span className="text-muted normal-case tracking-normal">(optional)</span></label>
          <input
            type="text"
            value={value.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            placeholder={label}
            className="w-full mt-1.5 px-3 py-2 border border-soft rounded-md focus:border-gold outline-none text-sm"
            data-testid={`${testidPrefix}-name`}
          />
        </div>

        <div>
          <label className="label-eyebrow !text-[10px]">Date of birth</label>
          <input
            type="date"
            required
            value={value.date || ""}
            onChange={(e) => setField("date", e.target.value)}
            className="w-full mt-1.5 px-3 py-2 border border-soft rounded-md focus:border-gold outline-none text-sm bg-transparent"
            data-testid={`${testidPrefix}-date`}
          />
        </div>

        <div>
          <label className="label-eyebrow !text-[10px]">Time of birth</label>
          <div className="flex items-center gap-2 mt-1.5">
            <select
              required
              value={value.hour || ""}
              onChange={(e) => setField("hour", e.target.value)}
              className="flex-1 px-3 py-2 border border-soft rounded-md focus:border-gold outline-none text-sm bg-bg"
              data-testid={`${testidPrefix}-hour`}
            >
              <option value="">Hour</option>
              {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span className="text-muted">:</span>
            <select
              required
              value={value.minute || ""}
              onChange={(e) => setField("minute", e.target.value)}
              className="flex-1 px-3 py-2 border border-soft rounded-md focus:border-gold outline-none text-sm bg-bg"
              data-testid={`${testidPrefix}-minute`}
            >
              <option value="">Min</option>
              {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <span className="text-muted text-xs">24-hr</span>
          </div>
        </div>

        <div ref={wrapperRef} className="relative">
          <label className="label-eyebrow !text-[10px]">Place of birth</label>
          <div className="relative mt-1.5">
            <input
              type="text"
              required
              placeholder="City — e.g. Mumbai"
              value={value.place || ""}
              onChange={(e) => onPlaceChange(e.target.value)}
              autoComplete="off"
              className="w-full px-3 py-2 pr-9 border border-soft rounded-md focus:border-gold outline-none text-sm"
              data-testid={`${testidPrefix}-place`}
            />
            <MapPin size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted" />
          </div>
          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
              {searching && (
                <div className="px-3 py-2 text-xs text-muted flex items-center gap-2">
                  <span className="inline-block w-3 h-3 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                  Searching…
                </div>
              )}
              {!searching && (value.place || "").trim().length >= 2 && suggestions.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted">No matches. Try a fuller city name.</div>
              )}
              {suggestions.map((s, i) => (
                <button
                  type="button"
                  key={`${s.lat}-${s.lon}-${i}`}
                  onClick={() => pickSuggestion(s)}
                  className="w-full text-left px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors border-b border-soft/40 last:border-0"
                  data-testid={`${testidPrefix}-place-option-${i}`}
                >
                  <div className="text-sm">{s.name}</div>
                  <div className="text-xs text-muted mt-0.5">{s.timezone}</div>
                </button>
              ))}
            </div>
          )}
          {value.timezone && (
            <p className="text-muted text-[10px] mt-1">Timezone: <span className="text-gold font-mono">{value.timezone}</span></p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CompatibilityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [boy, setBoy] = useState({ name: "", date: "", hour: "", minute: "", place: "", timezone: "" });
  const [girl, setGirl] = useState({ name: "", date: "", hour: "", minute: "", place: "", timezone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-prefill `boy` from signed-in user's profile (one-shot via shared
  // hook). Assumes the signed-in user is computing for themselves.
  useBirthDetailsPrefill((bd, u) => {
    if (!bd?.date || !bd?.time || !bd?.place) return;
    const [hh, mm] = String(bd.time).split(":");
    setBoy((prev) => ({
      ...prev,
      name: prev.name || u?.name || "",
      date: prev.date || bd.date,
      hour: prev.hour !== "" ? prev.hour : (hh || ""),
      minute: prev.minute !== "" ? prev.minute : (mm || ""),
      place: prev.place || bd.place,
      timezone: prev.timezone || bd.timezone || "Asia/Kolkata",
    }));
  });

  // Saved-report loader — for compatibility we just re-hydrate the result;
  // the form values aren't critical because the rendered output is self-contained.
  useSavedReportLoader({
    expectedTool: "compatibility",
    setForm: () => { /* boy/girl pre-fill is non-trivial; report alone is enough to render */ },
    setResult,
  });

  const ready = (p) => p.date && p.hour !== "" && p.minute !== "" && p.place && p.timezone;
  const canSubmit = ready(boy) && ready(girl);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const toPayload = (p) => ({
        date: p.date,
        time: `${p.hour}:${p.minute}`,
        place: p.place,
        timezone: p.timezone,
      });
      const { data } = await api.post("/compatibility/compute", {
        boy: toPayload(boy),
        girl: toPayload(girl),
      });
      setResult({ ...data, _names: { boy: boy.name?.trim() || "Partner 1", girl: girl.name?.trim() || "Partner 2" } });
      setTimeout(() => {
        document.getElementById("compat-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute compatibility. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <SEO
        title="Kundali Matching Online — Free Ashta-koota Guna Milan (36 points)"
        description="Free Vedic Kundali Matching using the classical 36-point Ashta-koota Guna Milan. Compute Varna, Vashya, Tara, Yoni, Graha-Maitri, Gana, Bhakoot, Nadi from Swiss-Ephemeris moon-nakshatras. Manglik & Nadi dosha check included."
        path="/compatibility"
        keywords={[
          "kundali matching", "kundli matching online", "kundali matching by name",
          "kundali milan", "guna milan", "ashta koota matching",
          "marriage compatibility", "horoscope matching", "manglik dosha matching",
          "nadi dosha", "bhakoot dosha", "rashi matching",
          "free kundli matching", "vedic compatibility", "love compatibility chart",
        ]}
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-16 pb-24" data-testid="compatibility-page">
        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Heart size={14} weight="fill" /> Free · Vedic guna milan
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light tracking-tight leading-[1.1]">
          Compatibility <span className="italic text-gold">— Ashta-koota</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          The classical 36-point Vedic guna-milan check used in traditional matchmaking — covering Varna, Vashya, Tara, Yoni, Graha-Maitri, Gana, Bhakoot and Nadi. We compute both moon-nakshatras from Swiss Ephemeris and score each koota with textbook Parashari rules.
        </p>

        <form onSubmit={onSubmit} className="mt-10" data-testid="compatibility-form">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <PartnerForm
              label="Partner 1 (Boy)"
              accent="border-[#D4AF37]/40"
              value={boy}
              onChange={setBoy}
              testidPrefix="boy"
            />
            <PartnerForm
              label="Partner 2 (Girl)"
              accent="border-[#FF6B9D]/40"
              value={girl}
              onChange={setGirl}
              testidPrefix="girl"
            />
          </div>

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="btn-primary rounded-full w-full sm:w-auto sm:px-12 mt-6 py-3.5 font-medium disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
            data-testid="compat-submit-btn"
          >
            <Sparkle size={16} weight="fill" />
            {submitting ? "Computing your guna milan..." : "Check compatibility"}
          </button>
        </form>

        <div id="compat-result" className="mt-12">
          {result && (
            <div className="space-y-5 anim-fadeup" data-testid="compat-result">
              {/* Score card */}
              <div className={`card-surface rounded-md p-6 sm:p-8 border-2 ${
                result.verdict_color === "emerald" ? "border-emerald-300" :
                result.verdict_color === "amber" ? "border-amber-300" :
                "border-rose-300"
              }`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="label-eyebrow text-gold">Guna milan result</div>
                    <div className="font-serif-display text-2xl sm:text-3xl mt-2 font-light">
                      {result._names.boy} <span className="text-muted">&</span> {result._names.girl}
                    </div>
                    <div className={`text-xs sm:text-sm font-medium mt-2 ${
                      result.verdict_color === "emerald" ? "text-emerald-700" :
                      result.verdict_color === "amber" ? "text-amber-700" :
                      "text-rose-700"
                    }`}>
                      {result.verdict}
                    </div>
                  </div>
                  <div className="text-right" data-testid="compat-score">
                    <div className="font-serif-display text-5xl sm:text-6xl font-light text-gold leading-none">{result.total_score}</div>
                    <div className="text-muted text-xs mt-1">out of {result.max_score} gunas</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-5 h-2 bg-[#FAF4E8] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      result.verdict_color === "emerald" ? "bg-emerald-500" :
                      result.verdict_color === "amber" ? "bg-amber-500" :
                      "bg-rose-400"
                    }`}
                    style={{ width: `${(result.total_score / result.max_score) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-muted mt-1.5">
                  <span>Below 18 · review</span>
                  <span>18+ · acceptable</span>
                  <span>24+ · good</span>
                  <span>32+ · excellent</span>
                </div>

                <p className="text-muted text-sm mt-5 font-light leading-relaxed">{result.summary}</p>

                <div className="grid grid-cols-2 gap-3 mt-5 text-xs">
                  <div className="card-surface rounded-md p-3 bg-[#FFF6E0]/40">
                    <div className="label-eyebrow !text-[10px]">{result._names.boy}</div>
                    <div className="font-medium mt-1">{result.boy.moon_rashi} · {result.boy.nakshatra} <span className="text-muted">(p{result.boy.pada})</span></div>
                  </div>
                  <div className="card-surface rounded-md p-3 bg-[#FFF0F4]/40">
                    <div className="label-eyebrow !text-[10px]">{result._names.girl}</div>
                    <div className="font-medium mt-1">{result.girl.moon_rashi} · {result.girl.nakshatra} <span className="text-muted">(p{result.girl.pada})</span></div>
                  </div>
                </div>
              </div>

              {/* Doshas + Kootas + CTA — PREMIUM */}
              <PaywallGate
                tool="compatibility"
                toolLabel="Compatibility · Guna Milan"
                birthDetails={{
                  date: boy.date,
                  time: `${boy.hour}:${boy.minute}`,
                  place: `${boy.place} | ${girl.name || "girl"} ${girl.date} ${girl.hour}:${girl.minute} ${girl.place}`,
                  timezone: boy.timezone,
                }}
                report={result}
              >
                <div className="space-y-5">
                  {/* Doshas (if any) */}
                  {result.doshas?.length > 0 && (
                    <div className="card-surface rounded-md p-5 sm:p-7 border border-rose-200 bg-rose-50/50" data-testid="compat-doshas">
                      <div className="flex items-center gap-2 label-eyebrow text-rose-800">
                        <Warning size={14} weight="fill" /> Doshas detected
                      </div>
                      <div className="mt-3 space-y-2.5">
                        {result.doshas.map((d, i) => (
                          <div key={i} className="flex items-start gap-2.5 text-sm">
                            <Warning size={16} className="text-rose-600 flex-shrink-0 mt-0.5" weight="fill" />
                            <div>
                              <div className="font-medium text-[#3F2D1B]">{d.name}</div>
                              <div className="text-muted text-xs mt-0.5">{d.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-rose-700/80 text-xs mt-4 font-light">
                        Doshas don&apos;t automatically rule out a match — remedies and parihara exist for most. Speak to an astrologer below for specifics.
                      </p>
                    </div>
                  )}

                  {/* Kootas breakdown */}
                  <div className="card-surface rounded-md p-5 sm:p-7" data-testid="compat-kootas">
                    <div className="label-eyebrow text-gold">All 8 kootas · classical breakdown</div>
                    <div className="mt-4 space-y-3">
                      {result.kootas.map((k, i) => {
                        const pct = (k.score / k.max) * 100;
                        const ok = pct >= 60;
                        return (
                          <div key={k.name} className="border-b border-soft last:border-0 pb-3 last:pb-0" data-testid={`koota-${k.name.toLowerCase().replace(/ /g, '-')}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-2 min-w-0">
                                {ok ? (
                                  <CheckCircle size={16} weight="fill" className="text-emerald-600 flex-shrink-0" />
                                ) : (
                                  <Warning size={16} weight="fill" className="text-amber-600 flex-shrink-0" />
                                )}
                                <span className="font-medium text-sm">{i + 1}. {k.name}</span>
                                <span className="text-muted text-xs">— {k.meaning}</span>
                              </div>
                              <div className={`font-mono text-sm font-semibold ${ok ? "text-emerald-700" : "text-amber-700"}`}>
                                {k.score} / {k.max}
                              </div>
                            </div>
                            <div className="mt-1.5 text-xs text-muted leading-relaxed">
                              {result._names.boy}: <span className="text-[#3F2D1B] font-medium">{k.boy_value}</span> · {result._names.girl}: <span className="text-[#3F2D1B] font-medium">{k.girl_value}</span>
                            </div>
                            <div className="text-xs text-muted mt-1 italic">{k.verdict}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </PaywallGate>

              {/* Funnel teaser — compatibility users → 20-Year Report */}
              <PremiumTeaser
                testid="compat-premium-teaser"
                topLine={<>
                  You&apos;ve matched two charts.<br/>
                  <span className="text-amber-300">Now unlock 20 years of what happens next.</span>
                </>}
                subLine={<>
                  Your <b className="text-amber-100">{result.total_score}/{result.max_score} gunas</b> is
                  just the surface. The full 55+ page 20-Year Report for {result._names.boy || "Partner 1"} reveals Sade Sati
                  windows, 6 doshas including Manglik severity, gem &amp; rudraksha for marital harmony, dasha-bhukti
                  timing for marriage and year-by-year career / wealth / health scores.
                </>}
              />

              {/* CTA — D9 Marriage specialist */}
              <ToolAstrologerCta
                requiredTechnique="Navamsa (D9) Marriage Reading"
                pitch={result.total_score >= 24
                  ? `Your ${result.total_score}/36 is strong — want a live D9-based marriage-timing & muhurat read?`
                  : `Your ${result.total_score}/36 needs a deeper look — get a live D9 reading on doshas, parihara & timing`}
                ctaLabel="Talk to"
              />
              <div className="text-center">
                <button
                  onClick={() => navigate("/kundali-calculator")}
                  className="btn-ghost rounded-full px-6 py-3 font-medium text-sm"
                  data-testid="compat-kundali-cta"
                >
                  Get individual kundalis →
                </button>
              </div>
            </div>
          )}

          {!result && (
            <div className="card-surface rounded-md p-8 sm:p-12 text-center" data-testid="compat-placeholder">
              <Heart size={36} weight="duotone" className="text-gold opacity-70 mx-auto" />
              <p className="font-serif-display text-2xl sm:text-3xl mt-3 font-light">Your guna milan opens here</p>
              <p className="text-muted text-sm mt-2 max-w-md mx-auto">
                Enter both partners&apos; birth details — date, time and place — to see the full 36-point Ashta-koota breakdown with verdicts and any doshas.
              </p>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
